import * as Sentry from '@sentry/vue';
import type { DiagnosticReport } from './diagnostics';
import type { NormalizedError } from './errorTypes';

/**
 * Sentry capture, plus the guards that keep a failing app from making itself worse.
 *
 * One capture per error. The previous code called `captureException` *and* `captureMessage`, which
 * doubled every event and put raw server text — potentially echoing a field value — into Sentry issue
 * titles, where it is indexed and searchable.
 */

/** Re-entrancy latch: a crash inside the crash handler degrades to one console line. */
let inReport = false;

interface Occurrence {
  count: number;
  firstAt: number;
  lastAt: number;
}

const occurrences = new Map<string, Occurrence>();

/** Long enough to swallow a retry storm, short enough that a genuine second failure still shows. */
const DEDUPE_WINDOW_MS = 4000;
const ESCALATE_WINDOW_MS = 30000;
const ESCALATE_AFTER = 5;

/**
 * Bound on distinct fingerprints held at once.
 *
 * A long session that keeps producing *new* failures would otherwise grow this map for as long as the
 * tab lives. Eviction is oldest-touched-first, which is also the only safe order: the entries that
 * matter are the ones still firing, and an evicted fingerprint costs at most one extra toast.
 */
const MAX_FINGERPRINTS = 200;

const evictStaleOccurrences = (): void => {
  if (occurrences.size <= MAX_FINGERPRINTS) {
    return;
  }
  const byAge = [...occurrences.entries()].sort((a, b) => a[1].lastAt - b[1].lastAt);
  byAge.slice(0, occurrences.size - MAX_FINGERPRINTS).forEach(([fingerprint]) => {
    occurrences.delete(fingerprint);
  });
};

export const isReportingAvailable = (): boolean => {
  try {
    return Sentry.isEnabled();
  } catch {
    return false;
  }
};

export const lastSentryEventId = (): string | undefined => {
  try {
    return Sentry.lastEventId();
  } catch {
    return undefined;
  }
};

export const currentReplayId = (): string | undefined => {
  try {
    return Sentry.getReplay()?.getReplayId();
  } catch {
    return undefined;
  }
};

export interface PresentDecision {
  present: boolean;
  occurrences: number;
  /** True on the Nth identical failure — the caller shows one sticky "this keeps failing" instead. */
  escalated: boolean;
}

/**
 * Whether this error is worth putting on screen again.
 *
 * Sentry's own `dedupeIntegration` handles the event side; this is purely about not stacking six
 * identical toasts while a request retries.
 */
export const shouldPresent = (normalized: NormalizedError): PresentDecision => {
  const now = Date.now();
  const seen = occurrences.get(normalized.fingerprint);

  if (!seen) {
    occurrences.set(normalized.fingerprint, { count: 1, firstAt: now, lastAt: now });
    evictStaleOccurrences();
    return { present: true, occurrences: 1, escalated: false };
  }

  const withinEscalateWindow = now - seen.firstAt < ESCALATE_WINDOW_MS;
  seen.count += 1;
  if (!withinEscalateWindow) {
    seen.count = 1;
    seen.firstAt = now;
  }
  const wasRecent = now - seen.lastAt < DEDUPE_WINDOW_MS;
  seen.lastAt = now;

  if (seen.count === ESCALATE_AFTER) {
    return { present: true, occurrences: seen.count, escalated: true };
  }
  return { present: !wasRecent, occurrences: seen.count, escalated: false };
};

export const resetPresentationState = (): void => {
  occurrences.clear();
};

const tagsFor = (normalized: NormalizedError, report: DiagnosticReport): Record<string, string> => {
  const tags: Record<string, string | undefined> = {
    'error.reference': normalized.reference,
    'error.kind': normalized.kind,
    'error.expected': String(!normalized.isReportable),
    'error.source': normalized.source,
    'http.status': normalized.httpStatus != null ? String(normalized.httpStatus) : undefined,
    'error.code': normalized.code,
    // Indexed and filterable, unlike the `diagnostic` context — this is what turns "some 500" into
    // "the 500 on this specific record".
    'study.id': report.context.studyIdentifier ?? report.context.studyB64Identifier,
    'case.id': report.context.caseIdentifier,
    'entry.id': report.context.entryIdentifier,
    'form.id': report.context.formIdentifier,
    'route.name': report.route?.name,
    release: report.release
  };

  return Object.fromEntries(
    Object.entries(tags).filter((entry): entry is [string, string] => entry[1] != null)
  );
};

/** Capture once and hand back the event id, which becomes `associatedEventId` on any feedback. */
export const reportToSentry = (normalized: NormalizedError, report: DiagnosticReport): string | undefined => {
  if (!normalized.isReportable) {
    return undefined;
  }

  try {
    return Sentry.captureException(normalized.cause, {
      captureContext: {
        tags: tagsFor(normalized, report),
        contexts: {
          diagnostic: { ...report, stack: undefined, trail: undefined },
          trail: { entries: report.trail }
        }
      }
    });
  } catch {
    /* eslint-disable-next-line no-console */
    console.error('[errors] capture failed', normalized.reference);
    return undefined;
  }
};

export interface FeedbackParams {
  report: DiagnosticReport;
  note: string;
  name?: string;
  email?: string;
  /** The full formatted report; `captureFeedback` requires a non-empty message. */
  formatted: string;
}

/**
 * Send the user's note to Sentry, linked to the error event and its session replay.
 *
 * `associatedEventId` falls back to `lastEventId()` for errors Sentry captured on our behalf — by the
 * time a human has typed a note and clicked send, it is populated.
 */
export const submitFeedbackToSentry = (params: FeedbackParams): string | undefined => {
  const { report, note, name, email, formatted } = params;

  try {
    return Sentry.captureFeedback(
      {
        message: note ? `${note}\n\n---\n${formatted}` : formatted,
        name,
        email,
        url: window.location.pathname,
        source: 'sp-data-error-report',
        associatedEventId: report.sentryEventId ?? lastSentryEventId(),
        tags: {
          'error.reference': report.reference,
          'error.kind': report.kind,
          release: report.release
        }
      },
      { includeReplay: true }
    );
  } catch {
    return undefined;
  }
};

/**
 * Run the reporting path with the re-entrancy latch held.
 *
 * Anything that reports an error must go through here, so a fault in reporting cannot recurse into
 * itself. Returns `undefined` when a report is already in flight.
 */
export const withReportGuard = <T>(work: () => T): T | undefined => {
  if (inReport) {
    return undefined;
  }
  inReport = true;
  try {
    return work();
  } catch (guardError) {
    /* eslint-disable-next-line no-console */
    console.error('[errors] handler failed', guardError);
    return undefined;
  } finally {
    inReport = false;
  }
};
